import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail, todayIST } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Calendar, Stethoscope, Clock, XCircle, Plus, User, Search, ArrowRightCircle } from "lucide-react";
import { toast } from "sonner";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Returns "YYYY-MM-DD" + "HH:MM" tuple for *now* in IST regardless of browser timezone.
function nowIST() {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(new Date()).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    time: `${parts.hour === "24" ? "00" : parts.hour}:${parts.minute}`,
  };
}

// Filter out past slots if the picked date is today (IST).
// Keeps the slot whose end-time is still in the future so the patient can be booked into the *current* slot.
function visibleSlots(slotsList, dateStr) {
  if (!slotsList || !dateStr) return slotsList || [];
  const now = nowIST();
  if (dateStr !== now.date) return slotsList; // future or past date — show everything (backend won't return past dates anyway)
  return slotsList.filter((s) => {
    const endTime = s.end || s.time; // fallback if end missing
    return endTime > now.time;
  });
}

function statusClass(s) {
  return s === "completed" ? "border-leaf/40 bg-leaf-50 text-leaf-700"
    : s === "cancelled" ? "border-rose-200 bg-rose-50 text-rose-700"
    : s === "no_show" ? "border-amber-200 bg-amber-50 text-amber-700"
    : "border-brand/30 bg-brand/5 text-brand";
}

function BookAppointmentDialog({ open, onOpenChange, prefillPatientId, onBooked }) {
  const { branch } = useAuth();
  const [doctors, setDoctors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [patientQuery, setPatientQuery] = useState("");
  const [patientResults, setPatientResults] = useState([]);
  const [patient, setPatient] = useState(null);
  const [doctorId, setDoctorId] = useState("");
  const [doctorBranchIds, setDoctorBranchIds] = useState([]);  // branches the doctor is assigned to (intersection with availability)
  const [chartBranchId, setChartBranchId] = useState("");
  const [chart, setChart] = useState(null);
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState(null);
  const [picked, setPicked] = useState("");
  const [type, setType] = useState("new");
  const [notes, setNotes] = useState("");
  const [paymentIntent, setPaymentIntent] = useState("later");  // later | online
  const [paymentMode, setPaymentMode] = useState("cash");
  const [paymentRef, setPaymentRef] = useState("");
  const [busy, setBusy] = useState(false);
  // Quick new-patient registration when search yields no matches
  const [newPatientOpen, setNewPatientOpen] = useState(false);
  const [newPatient, setNewPatient] = useState({ name: "", mobile: "", gender: "male", age: "", branch_id: "" });
  const [creatingPatient, setCreatingPatient] = useState(false);

  useEffect(() => {
    if (!open) return;
    Promise.all([api.get("/hospital/doctors"), api.get("/hospital/branches")]).then(([d, b]) => {
      setDoctors(d.data.items);
      setBranches(b.data.items);
    });
    setPatient(null); setPatientQuery(""); setPatientResults([]);
    setDoctorId(""); setDoctorBranchIds([]); setChartBranchId(""); setChart(null);
    setDate(""); setSlots(null); setPicked("");
    setType("new"); setNotes("");
    setPaymentIntent("later"); setPaymentMode("cash"); setPaymentRef("");
    setNewPatientOpen(false); setNewPatient({ name: "", mobile: "", gender: "male", age: "", branch_id: branch?.id || "" });
    if (prefillPatientId) {
      api.get(`/patients/${prefillPatientId}`).then(({ data }) => setPatient(data.patient)).catch(() => {});
    }
  }, [open, prefillPatientId]);

  const searchPatients = async (q) => {
    setPatientQuery(q);
    if (q.length < 2) { setPatientResults([]); return; }
    const { data } = await api.get(`/patients?q=${encodeURIComponent(q)}&limit=10`);
    setPatientResults(data.items || []);
  };

  // Quick-register a new patient when the search returns nothing
  const createPatientInline = async () => {
    if (!newPatient.name || !newPatient.mobile) {
      toast.error("Name and mobile are required to register a patient.");
      return;
    }
    setCreatingPatient(true);
    try {
      const payload = {
        ...newPatient,
        age: newPatient.age ? Number(newPatient.age) : null,
        branch_id: newPatient.branch_id || branch?.id || (branches[0] && branches[0].id),
        category: "cash",
        referral_source: "Walk-in",
        auto_invoice: false,  // appointment booking will charge separately
      };
      const { data } = await api.post("/patients", payload);
      toast.success(`Patient registered — UHID ${data.uhid}`);
      // Fetch full patient record (so we have id/name/uhid/mobile) and select it
      const { data: full } = await api.get(`/patients/${data.id}`);
      setPatient(full.patient);
      setNewPatientOpen(false);
      setPatientQuery(""); setPatientResults([]);
      setNewPatient({ name: "", mobile: "", gender: "male", age: "", branch_id: branch?.id || "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not register the patient.");
    } finally { setCreatingPatient(false); }
  };

  const onDoctorSelect = (did) => {
    setDoctorId(did);
    setChart(null); setChartBranchId(""); setDate(""); setSlots(null); setPicked("");
    if (!did) return;
    const doc = doctors.find((d) => d.id === did);
    // Branches the doctor actually has availability windows in
    const avail = doc?.availability || [];
    const branchIds = [...new Set(avail.map((w) => w.branch_id))];
    setDoctorBranchIds(branchIds);
    // Default to current session branch when the doctor works there; else first available
    const defaultBranch = branchIds.includes(branch?.id) ? branch.id : (branchIds[0] || "");
    if (defaultBranch) loadChart(did, defaultBranch);
  };

  const loadChart = async (did, bid) => {
    setChartBranchId(bid);
    setChart(null); setDate(""); setSlots(null); setPicked("");
    if (!did || !bid) return;
    const { data } = await api.get(`/appointments/doctor/${did}/week?branch_id=${bid}`);
    setChart(data);
  };

  const loadSlots = async (d) => {
    setDate(d); setSlots(null); setPicked("");
    if (!d || !doctorId || !chartBranchId) return;
    const { data } = await api.get(`/appointments/slots?doctor_id=${doctorId}&branch_id=${chartBranchId}&date=${d}`);
    setSlots(data);
  };

  const pickDay = (dayIndex) => {
    // Click on Mon-Sun cell → set date to next occurrence of that weekday from today (IST).
    if (!chart?.week?.[dayIndex]?.length) return;
    const todayStr = todayIST();  // "YYYY-MM-DD" in IST
    // Build the date at UTC midnight so toISOString().slice(0,10) preserves the IST calendar date.
    const [y, m, d] = todayStr.split("-").map(Number);
    const todayUtc = new Date(Date.UTC(y, m - 1, d));
    const todayDow = (todayUtc.getUTCDay() + 6) % 7;  // 0=Mon..6=Sun
    const diff = (dayIndex - todayDow + 7) % 7;
    const target = new Date(todayUtc.getTime() + diff * 86400000);
    const iso = target.toISOString().slice(0, 10);
    loadSlots(iso);
  };

  const book = async () => {
    if (!patient || !doctorId || !chartBranchId || !date || !picked) { toast.error("Please pick patient, doctor, branch, date and slot."); return; }
    setBusy(true);
    try {
      const body = {
        patient_id: patient.id, doctor_id: doctorId, branch_id: chartBranchId,
        scheduled_date: date, scheduled_time: picked, type, notes,
        payment_intent: paymentIntent,
        ...(paymentIntent === "online" ? { payment_mode: paymentMode, payment_reference: paymentRef } : {}),
      };
      const { data } = await api.post("/appointments", body);
      if (data.invoice_id) {
        toast.success(`Appointment booked & receipt generated — ${data.scheduled_date} ${data.scheduled_time}`);
        window.open(`/print/registration-receipt/${data.invoice_id}`, "_blank");
      } else {
        toast.success(`Appointment booked — pay on arrival · ${data.scheduled_date} ${data.scheduled_time}`);
      }
      onBooked?.(data);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const todayWeekIndex = (() => {
    const [y, m, d] = todayIST().split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    return (dt.getUTCDay() + 6) % 7;
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle className="font-heading text-lg flex items-center gap-2"><Calendar size={16} className="text-brand" />Book Appointment</DialogTitle></DialogHeader>
        <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1">
          {/* Step 1: Patient */}
          <Card className="p-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-brand mb-2">Patient</div>
            {patient ? (
              <div className="flex items-center justify-between rounded-md border border-leaf/30 bg-leaf-50 p-3" data-testid="booking-selected-patient">
                <div>
                  <div className="font-semibold">{patient.name}</div>
                  <div className="text-xs text-ink-muted">UHID {patient.uhid} · {patient.mobile}</div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => setPatient(null)} data-testid="booking-clear-patient">Change</Button>
              </div>
            ) : (
              <div>
                <div className="relative">
                  <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtle" />
                  <Input placeholder="Search patient by name / UHID / mobile (min 2 chars)…" value={patientQuery} onChange={(e) => searchPatients(e.target.value)} className="pl-9" data-testid="booking-patient-search" />
                </div>
                {patientResults.length > 0 && (
                  <div className="mt-2 max-h-44 overflow-y-auto rounded-md border border-border">
                    {patientResults.map((p) => (
                      <button key={p.id} className="flex w-full items-center justify-between gap-3 border-b border-border p-2 text-left text-sm last:border-b-0 hover:bg-secondary" onClick={() => { setPatient(p); setPatientResults([]); }} data-testid={`booking-patient-pick-${p.uhid}`}>
                        <span><b>{p.name}</b> <span className="text-xs text-ink-muted">· UHID {p.uhid} · {p.mobile}</span></span>
                        <User size={12} className="text-ink-muted" />
                      </button>
                    ))}
                  </div>
                )}
                {/* No results → offer quick new-patient registration */}
                {patientQuery.length >= 2 && patientResults.length === 0 && !newPatientOpen && (
                  <div className="mt-2 flex items-center justify-between rounded-md border border-dashed border-brand/40 bg-brand/5 p-2.5 text-xs">
                    <span className="text-ink">No patient found for <b>"{patientQuery}"</b>.</span>
                    <Button size="sm" variant="outline" className="border-brand text-brand hover:bg-brand hover:text-white" onClick={() => {
                      // Pre-fill name/mobile from the query when it looks like one
                      const isPhone = /^[6-9]\d{0,9}$/.test(patientQuery.trim());
                      setNewPatient((n) => ({
                        ...n,
                        name: isPhone ? n.name : (n.name || patientQuery.trim()),
                        mobile: isPhone ? patientQuery.trim() : n.mobile,
                        branch_id: n.branch_id || branch?.id || (branches[0] && branches[0].id) || "",
                      }));
                      setNewPatientOpen(true);
                    }} data-testid="booking-register-new-btn"><Plus size={11} className="mr-1" />Register new patient</Button>
                  </div>
                )}
                {/* Inline new-patient mini-form */}
                {newPatientOpen && (
                  <div className="mt-3 rounded-md border-2 border-brand/30 bg-brand/5 p-3" data-testid="booking-new-patient-form">
                    <div className="mb-2 flex items-center justify-between">
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-brand">New patient — quick registration</div>
                      <button type="button" onClick={() => setNewPatientOpen(false)} className="text-xs text-ink-muted hover:text-ink" data-testid="booking-new-patient-close"><XCircle size={14} /></button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="col-span-2">
                        <Label className="text-[10px]">Full name *</Label>
                        <Input value={newPatient.name} onChange={(e) => setNewPatient({ ...newPatient, name: e.target.value })} placeholder="e.g. Rajesh Kumar" className="h-9" data-testid="booking-new-patient-name" />
                      </div>
                      <div>
                        <Label className="text-[10px]">Mobile *</Label>
                        <Input value={newPatient.mobile} onChange={(e) => setNewPatient({ ...newPatient, mobile: e.target.value })} placeholder="10-digit" className="h-9" data-testid="booking-new-patient-mobile" />
                      </div>
                      <div>
                        <Label className="text-[10px]">Age</Label>
                        <Input type="number" value={newPatient.age} onChange={(e) => setNewPatient({ ...newPatient, age: e.target.value })} placeholder="years" className="h-9" data-testid="booking-new-patient-age" />
                      </div>
                      <div>
                        <Label className="text-[10px]">Gender</Label>
                        <Select value={newPatient.gender} onValueChange={(v) => setNewPatient({ ...newPatient, gender: v })}>
                          <SelectTrigger className="h-9" data-testid="booking-new-patient-gender"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="male">Male</SelectItem>
                            <SelectItem value="female">Female</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[10px]">Branch</Label>
                        <Select value={newPatient.branch_id} onValueChange={(v) => setNewPatient({ ...newPatient, branch_id: v })}>
                          <SelectTrigger className="h-9" data-testid="booking-new-patient-branch"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} ({b.code})</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="mt-3 flex justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => setNewPatientOpen(false)} className="h-8">Cancel</Button>
                      <Button size="sm" onClick={createPatientInline} disabled={creatingPatient} className="h-8 bg-brand hover:bg-brand-700" data-testid="booking-new-patient-save">
                        {creatingPatient ? "Registering…" : <><Plus size={11} className="mr-1" />Register &amp; select</>}
                      </Button>
                    </div>
                    <div className="mt-2 text-[10px] text-ink-muted">Need to capture photo, ABHA, ID proof or insurance? Use the full <a href="/app/patients/new" target="_blank" rel="noreferrer" className="text-brand underline">Patient registration screen</a> instead.</div>
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Step 2: Doctor + Branch */}
          <Card className="p-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-brand mb-2">Doctor & Branch</div>
            <Select value={doctorId} onValueChange={onDoctorSelect}>
              <SelectTrigger data-testid="booking-doctor-select"><SelectValue placeholder="Select doctor" /></SelectTrigger>
              <SelectContent>{doctors.map((d) => <SelectItem key={d.id} value={d.id}>Dr. {d.name} {d.qualification ? `— ${d.qualification}` : ""}</SelectItem>)}</SelectContent>
            </Select>
            {doctorBranchIds.length > 0 && (
              <div className="mt-2">
                <Label className="text-[11px] uppercase tracking-wider text-ink-muted">Practice branch</Label>
                <div className="mt-1 flex flex-wrap gap-1.5" data-testid="booking-branch-picker">
                  {doctorBranchIds.map((bid) => {
                    const b = branches.find((x) => x.id === bid);
                    if (!b) return null;
                    const active = chartBranchId === bid;
                    return (
                      <button
                        key={bid}
                        type="button"
                        onClick={() => loadChart(doctorId, bid)}
                        className={`rounded-full border px-3 py-1 text-xs ${active ? "border-brand bg-brand text-white" : "border-border bg-white hover:border-brand"}`}
                        data-testid={`booking-branch-pick-${b.code}`}
                      >
                        {b.name} · {b.code}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {chart && (
              <div className="mt-3 rounded-md border border-border p-3" data-testid="booking-week-chart">
                <div className="mb-1 flex items-center justify-between text-xs">
                  <span><b>Dr. {chart.doctor.name}</b> at <b>{branches.find((b) => b.id === chartBranchId)?.name}</b> · ₹ {chart.doctor.opd_rate}/visit · {chart.doctor.slot_minutes} min slots</span>
                </div>
                <div className="grid grid-cols-7 gap-1.5 text-[11px]">
                  {DAYS.map((d, idx) => {
                    const has = chart.week[idx]?.length > 0;
                    const isToday = idx === todayWeekIndex;
                    return (
                      <button
                        key={d}
                        type="button"
                        disabled={!has}
                        onClick={() => pickDay(idx)}
                        className={`rounded border p-1.5 text-left transition ${has ? "border-border bg-white hover:border-brand hover:bg-brand/5 cursor-pointer" : "border-border bg-secondary/50 cursor-not-allowed"} ${isToday ? "ring-2 ring-brand/30" : ""}`}
                        data-testid={`booking-day-${d}`}
                      >
                        <div className="font-semibold text-brand">{d}</div>
                        {has ? chart.week[idx].map((w, i) => (
                          <div key={i} className="mt-0.5 text-ink">{w.start_time}–{w.end_time}</div>
                        )) : <div className="text-ink-subtle">—</div>}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-2 text-[10px] text-ink-muted">Click a day to jump to the next available date for that weekday.</div>
              </div>
            )}
          </Card>

          {/* Step 3: Date + slot */}
          {doctorId && (
            <Card className="p-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-brand mb-2">Date & slot</div>
              <div className="flex flex-wrap items-center gap-3">
                <Input type="date" value={date} onChange={(e) => loadSlots(e.target.value)} className="max-w-xs" data-testid="booking-date-input" />
                {slots && (() => {
                  const visible = visibleSlots(slots.slots, date);
                  const hiddenCount = slots.slots.length - visible.length;
                  return (
                    <div className="text-xs text-ink-muted">
                      {visible.length} slot(s) on this day
                      {hiddenCount > 0 && <span className="ml-2 text-amber-600">· {hiddenCount} past slot(s) hidden</span>}
                    </div>
                  );
                })()}
              </div>
              {slots && (() => {
                const visible = visibleSlots(slots.slots, date);
                if (slots.slots.length === 0) {
                  return <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700">Doctor is not available at this branch on this date.</div>;
                }
                if (visible.length === 0) {
                  return <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700">All slots for today have passed. Pick a future date.</div>;
                }
                return (
                  <div className="mt-3 flex flex-wrap gap-1.5" data-testid="booking-slots">
                    {visible.map((s) => (
                      <button
                        key={s.time}
                        disabled={s.booked}
                        onClick={() => setPicked(s.time)}
                        className={`rounded-full border px-3 py-1 text-xs ${picked === s.time ? "border-brand bg-brand text-white" : s.booked ? "cursor-not-allowed border-rose-200 bg-rose-50 text-rose-500 line-through" : "border-border bg-white hover:border-brand"}`}
                        title={s.booked ? `Booked: ${s.patient_name}` : `Available ${s.time}–${s.end}`}
                        data-testid={`booking-slot-${s.time}`}
                      >
                        {s.time}
                      </button>
                    ))}
                  </div>
                );
              })()}
            </Card>
          )}

          {/* Step 4: Payment + Notes + Type */}
          <Card className="p-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-brand mb-2">Payment</div>
            <div className="flex gap-2" data-testid="booking-payment-intent">
              <button type="button" onClick={() => setPaymentIntent("later")} className={`flex-1 rounded-md border px-3 py-2 text-xs text-left ${paymentIntent === "later" ? "border-brand bg-brand/5" : "border-border bg-white hover:border-brand"}`} data-testid="booking-pay-later">
                <div className="font-semibold">Pay at hospital</div>
                <div className="text-[10px] text-ink-muted">Book only · convert to OPD on arrival</div>
              </button>
              <button type="button" onClick={() => setPaymentIntent("online")} className={`flex-1 rounded-md border px-3 py-2 text-xs text-left ${paymentIntent === "online" ? "border-brand bg-brand/5" : "border-border bg-white hover:border-brand"}`} data-testid="booking-pay-online">
                <div className="font-semibold">Pay online now</div>
                <div className="text-[10px] text-ink-muted">Generate receipt + add to OPD queue immediately</div>
              </button>
            </div>
            {paymentIntent === "online" && (
              <div className="mt-3 grid grid-cols-2 gap-3" data-testid="booking-pay-online-details">
                <div>
                  <Label>Payment mode</Label>
                  <Select value={paymentMode} onValueChange={(v) => { setPaymentMode(v); setPaymentRef(""); }}>
                    <SelectTrigger data-testid="booking-payment-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {paymentMode === "upi" && <div><Label>UPI txn no.</Label><Input value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} placeholder="UPI-XXX" data-testid="booking-pay-ref" /></div>}
                {paymentMode === "card" && <div><Label>Card last 4</Label><Input value={paymentRef} maxLength={4} onChange={(e) => setPaymentRef(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="0000" data-testid="booking-pay-ref" /></div>}
              </div>
            )}
          </Card>

          <Card className="p-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Visit type</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger data-testid="booking-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New consultation</SelectItem>
                    <SelectItem value="follow_up">Follow-up</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. requested by ref doctor" data-testid="booking-notes-input" />
              </div>
            </div>
          </Card>

          <Button onClick={book} disabled={busy} className="w-full bg-brand hover:bg-brand-700" data-testid="booking-confirm-btn">{busy ? "Booking…" : <>Confirm appointment <Clock size={14} className="ml-2" /></>}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Appointments() {
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const prefill = params.get("patient_id") || "";
  const [fromDate, setFromDate] = useState(todayIST());
  const [toDate, setToDate] = useState(() => {
    const d = new Date(todayIST()); d.setDate(d.getDate() + 14);
    return d.toISOString().slice(0, 10);
  });
  const [calendar, setCalendar] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");  // '' | 'scheduled' | 'completed' | ...
  // Convert-to-OPD payment dialog state
  const [convertTarget, setConvertTarget] = useState(null);  // appointment row
  const [convertMode, setConvertMode] = useState("cash");
  const [convertRef, setConvertRef] = useState("");
  const [convertBusy, setConvertBusy] = useState(false);

  const load = async () => {
    const params = new URLSearchParams({ from_date: fromDate, to_date: toDate });
    if (statusFilter) params.set("status", statusFilter);
    const { data } = await api.get(`/appointments?${params.toString()}`);
    setItems(data.items);
    const dsum = await api.get(`/appointments/day-summary?from=${fromDate}&to=${toDate}`).catch(() => ({ data: { days: [] } }));
    setCalendar(dsum.data.days || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fromDate, toDate, statusFilter]);
  useEffect(() => { if (prefill) setOpen(true); }, [prefill]);

  const cancel = async (a) => {
    try {
      await api.delete(`/appointments/${a.id}`);
      toast.success("Appointment cancelled");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const convert = async (a) => {
    // Already paid online → skip the dialog, just convert
    if (a.payment_status === "paid") {
      try {
        const { data } = await api.post(`/appointments/${a.id}/convert`, { payment_intent: "later" });
        toast.success(`Converted to OPD · Token #${data.token_no}`);
        if (data.invoice_id) window.open(`/print/registration-receipt/${data.invoice_id}`, "_blank");
        load();
      } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
      return;
    }
    // Unpaid → open payment-mode chooser
    setConvertMode("cash");
    setConvertRef("");
    setConvertTarget(a);
  };

  const confirmConvert = async () => {
    if (!convertTarget) return;
    setConvertBusy(true);
    try {
      const body = convertMode === "free"
        ? { payment_intent: "online", payment_mode: "free" }
        : { payment_intent: "online", payment_mode: convertMode, payment_reference: convertRef };
      const { data } = await api.post(`/appointments/${convertTarget.id}/convert`, body);
      toast.success(`Converted to OPD · Token #${data.token_no} · ${convertMode.toUpperCase()}`);
      if (data.invoice_id) window.open(`/print/registration-receipt/${data.invoice_id}`, "_blank");
      setConvertTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setConvertBusy(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="appointments-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="section-label">Front desk</div>
          <h1 className="font-heading text-3xl font-semibold">Appointments</h1>
          <p className="mt-1 text-sm text-ink-muted">{items.length} matching · book a slot against doctor's branch-wise availability.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-brand hover:bg-brand-700" data-testid="appointments-new-btn"><Plus size={14} className="mr-2" />Book new</Button>
      </div>

      {/* Calendar / day summary */}
      <Card className="p-4" data-testid="appointments-calendar">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <Label className="text-xs">From</Label>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-8 max-w-[160px]" data-testid="appt-from-date" />
          <Label className="text-xs">To</Label>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-8 max-w-[160px]" data-testid="appt-to-date" />
          <div className="ml-auto inline-flex overflow-hidden rounded-full border border-border text-xs">
            {[{ k: "", l: "All" }, { k: "scheduled", l: "Open" }, { k: "completed", l: "Closed" }, { k: "cancelled", l: "Cancelled" }].map((f) => (
              <button key={f.l} type="button" onClick={() => setStatusFilter(f.k)} className={`px-3 py-1 ${statusFilter === f.k ? "bg-brand text-white" : "bg-white text-ink hover:bg-secondary"}`} data-testid={`appt-filter-${f.l.toLowerCase()}`}>{f.l}</button>
            ))}
          </div>
        </div>
        {calendar.length > 0 && (
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
            {calendar.map((d) => (
              <button key={d.date} type="button" onClick={() => { setFromDate(d.date); setToDate(d.date); }} className="rounded-md border border-border bg-white p-2 text-left transition hover:border-brand" data-testid={`appt-day-${d.date}`}>
                <div className="text-[10px] uppercase tracking-wider text-ink-muted">{new Date(d.date).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", timeZone: "Asia/Kolkata" })}</div>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="font-heading text-lg font-semibold text-brand" data-testid={`appt-day-open-${d.date}`}>{d.open}</span>
                  <span className="text-[10px] text-ink-muted">open</span>
                  <span className="ml-auto font-heading text-lg font-semibold text-emerald-600" data-testid={`appt-day-closed-${d.date}`}>{d.closed}</span>
                  <span className="text-[10px] text-ink-muted">closed</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </Card>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-left text-[11px] uppercase tracking-wider text-ink-muted">
            <tr>
              <th className="px-4 py-3">When</th>
              <th className="px-4 py-3">Patient</th>
              <th className="px-4 py-3">Doctor</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Payment</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.map((a) => (
              <tr key={a.id} className="hover:bg-secondary/30" data-testid={`appointment-row-${a.id}`}>
                <td className="px-4 py-3 font-mono">{a.scheduled_date} · {a.scheduled_time}</td>
                <td className="px-4 py-3">
                  <button className="font-medium text-brand hover:underline" onClick={() => navigate(`/app/patients/${a.patient_id}`)}>{a.patient_name}</button>
                  <div className="text-[11px] text-ink-muted">UHID {a.patient_uhid} · {a.patient_mobile}</div>
                </td>
                <td className="px-4 py-3"><Stethoscope size={12} className="mr-1 inline text-brand" />Dr. {a.doctor_name}</td>
                <td className="px-4 py-3 capitalize">{a.type?.replace(/_/g, " ")}</td>
                <td className="px-4 py-3">
                  {a.payment_status === "paid"
                    ? <Badge variant="outline" className="border-leaf/40 bg-leaf-50 text-leaf-700">Paid · {a.payment_mode}</Badge>
                    : <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">Pay at hospital</Badge>}
                </td>
                <td className="px-4 py-3"><Badge variant="outline" className={statusClass(a.status)}>{a.status}</Badge></td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center justify-end gap-1.5">
                    {a.status === "scheduled" && !a.visit_id && (
                      <Button size="sm" className="bg-brand hover:bg-brand-700" onClick={() => convert(a)} title="Patient arrived — start OPD visit" data-testid={`appointment-convert-${a.id}`}>
                        <ArrowRightCircle size={12} className="mr-1" />Convert to OPD
                      </Button>
                    )}
                    {a.status === "scheduled" && (
                      <Button size="sm" variant="ghost" onClick={() => cancel(a)} title="Cancel" data-testid={`appointment-cancel-${a.id}`}><XCircle size={14} className="text-rose-500" /></Button>
                    )}
                    {a.visit_id && (
                      <Button size="sm" variant="outline" onClick={() => navigate(`/app/opd/consult/${a.visit_id}`)} title="Open in OPD" data-testid={`appointment-open-visit-${a.id}`}>
                        Open visit
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-ink-muted">No appointments in this range. Click "Book new".</td></tr>}
          </tbody>
        </table>
      </Card>

      <BookAppointmentDialog open={open} onOpenChange={setOpen} prefillPatientId={prefill} onBooked={() => load()} />

      {/* Convert-to-OPD payment chooser */}
      <Dialog open={!!convertTarget} onOpenChange={(v) => { if (!v) setConvertTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="convert-payment-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightCircle size={18} className="text-brand" />
              Collect payment & convert to OPD
            </DialogTitle>
          </DialogHeader>
          {convertTarget && (
            <div className="space-y-4 text-sm">
              <div className="rounded-md border border-border bg-secondary/40 p-3">
                <div className="font-medium">{convertTarget.patient_name}</div>
                <div className="text-[11px] text-ink-muted">UHID {convertTarget.patient_uhid} · Dr. {convertTarget.doctor_name}</div>
                <div className="mt-1 text-[11px] text-ink-muted">{convertTarget.scheduled_date} · {convertTarget.scheduled_time}</div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-ink-muted">Payment mode</Label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {[
                    { k: "cash", l: "Cash" },
                    { k: "upi", l: "UPI" },
                    { k: "card", l: "Card" },
                    { k: "free", l: "Free / Waived" },
                  ].map((m) => (
                    <button
                      key={m.k}
                      type="button"
                      onClick={() => setConvertMode(m.k)}
                      data-testid={`convert-mode-${m.k}`}
                      className={`rounded-md border px-3 py-2 text-sm transition ${convertMode === m.k ? "border-brand bg-brand text-white" : "border-border bg-white text-ink hover:border-brand"}`}
                    >
                      {m.l}
                    </button>
                  ))}
                </div>
              </div>

              {(convertMode === "upi" || convertMode === "card") && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-ink-muted">
                    {convertMode === "upi" ? "UPI reference / txn id (optional)" : "Card last-4 / approval code (optional)"}
                  </Label>
                  <Input
                    value={convertRef}
                    onChange={(e) => setConvertRef(e.target.value)}
                    placeholder={convertMode === "upi" ? "e.g. 4XB87QW2NP" : "e.g. 4242 · APR-12345"}
                    className="mt-1"
                    data-testid="convert-payment-reference"
                  />
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setConvertTarget(null)} disabled={convertBusy} data-testid="convert-cancel">Cancel</Button>
                <Button className="bg-brand hover:bg-brand-700" onClick={confirmConvert} disabled={convertBusy} data-testid="convert-confirm">
                  {convertBusy ? "Converting…" : <>Convert & print receipt <ArrowRightCircle size={14} className="ml-2" /></>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
